import { useDebounceFn } from '@vueuse/core';
import type { PiniaPluginContext, Store } from 'pinia';

import {
  PERSISTED_STORE_KEY_PREFIX,
  persistedStoreStorage,
} from '#ui-shared/pinia/persist/local-storage-pinia-storage';
import type { PiniaPersistStorage } from '#ui-shared/pinia/persist/persisted-store.types';
import { isWebViewMode } from '#ui-shared/utils/webview-utils';

const PERSIST_DEBOUNCE_MS = 1000;

export function persistedStorePlugin(): (context: PiniaPluginContext) => void {
  return function (context: PiniaPluginContext) {
    createPersistedStore(context);
  };
}

export function createPersistedStore(context: PiniaPluginContext): void {
  const store = context.store;

  // Opt-in only, and webview only: the standalone web app always bootstraps from the server.
  if (context.options.persist !== true || !isWebViewMode()) {
    return;
  }

  // At creation is the only moment that works: state arriving from the native layer lands either
  // before a store exists or after it has already loaded. This was the bug.
  const persisted = persistedStoreStorage.getItem(`${PERSISTED_STORE_KEY_PREFIX}${store.$id}`);

  if (persisted !== null) {
    hydrateStore(store, persisted);
  }

  const debouncedPersist = useDebounceFn(
    () => {
      persistState(store);
    },
    PERSIST_DEBOUNCE_MS,
    {
      maxWait: PERSIST_DEBOUNCE_MS * 2,
    },
  );

  // Detached: persistence outlives whichever component happened to touch the store first.
  store.$subscribe(() => {
    void debouncedPersist();
  }, { detached: true });
}

function hydrateStore(store: Store, data: string): void {
  try {
    const deserialized = JSON.parse(data);

    store.$patch(deserialized);
  }
  catch (error) {
    console.error(`[persisted-store] Could not restore ${store.$id}`, error);
  }
}

/** Exported for tests; production writes go through the debounced subscription above. */
export function persistState(store: Store, storage: PiniaPersistStorage = persistedStoreStorage): void {
  try {
    // Through the reactive proxy: a setup store's raw `$state` holds the refs, which are circular.
    const toStorage = JSON.stringify(store.$state);

    storage.setItem(`${PERSISTED_STORE_KEY_PREFIX}${store.$id}`, toStorage);
  }
  catch (error) {
    console.error(`[persisted-store] Could not serialize ${store.$id}`, error);
  }
}
